import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Chip, TablePagination } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { config } from '../../config';
import { showSuccessSnackbar, showErrorSnackbar } from '../../utils/Snackbar';
import { CommHandler } from '../../utils';
import { useCheckPermissions } from '../../utils/roles/permissionUtils';

const StyledTableContainer = styled(TableContainer)`
  max-height: 700px;
  overflow-y: auto;

  /* Custom scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px; /* Wider scrollbar */
    height: 8px; /* Taller scrollbar for horizontal */
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, #6a5acd, #836fff); /* Gradient color */
    border-radius: 10px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3); /* Shadow effect */
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to bottom, #836fff, #6a5acd); /* Reverse gradient on hover */
  }

  &::-webkit-scrollbar-track {
    background: linear-gradient(to bottom, #f0f0f0, #e0e0e0); /* Lighter gradient */
    border-radius: 10px; /* Rounded corners */
  }

  /* For Firefox */
  scrollbar-width: thin; /* Thin scrollbar */
  scrollbar-color: #ccc #f0f0f0; /* Thumb and track colors */
`;

interface LevelMapping {
  id: number;
  level: string; // This will store the display level like "L5"
  levelNumber: number;
  categories: string[];
  isPlaceholder?: boolean; // Flag to indicate if this level is not configured yet
  fullLevelString?: string; // Store the complete level string from API like "5: f"
}

// New interfaces for API response
interface ApiMetricResponse {
  id: number;
  level: string; // Changed from number to string to match API response
  weightage_id: number;
  createdAt: string;
  updatedAt: string;
  weightage: {
    category_id: number;
    weightage: number;
    allDepartment: boolean;
    category: {
      id: number;
      name: string;
    };
  };
}

const SlabMapping: React.FC = () => {
  useEffect(() => {
    const title = document.querySelector('title');
    if (title) {
      title.innerText = 'Slab Mapping Configuration';
    }
  }, []);

  const navigate = useNavigate();

  // Helper function to extract level number from full level string (e.g., "1: hello" -> 1)
  const extractLevelNumber = (fullLevelString: string): number => {
    if (typeof fullLevelString !== 'string') {
      return 0;
    }
    const match = fullLevelString.match(/^(\d+):/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const [levels, setLevels] = useState<LevelMapping[]>([]);
  const [availableLevels, setAvailableLevels] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const hasEditPermission = useCheckPermissions('ADSLM', ['Edit']);

  const notify = useCallback((type: 'success' | 'info' | 'warning', message: string) => {
    if (type === 'success') {
      showSuccessSnackbar(message);
    } else {
      showErrorSnackbar(message);
    }
  }, []);

  const fetchAvailableLevels = useCallback(
    async (showSuccess = true) => {
      try {
        const response = await CommHandler.request({
          url: `${config.apiBaseUrl}/evaluation/designation-group/levels`,
          method: 'GET',
        });

        if (Array.isArray(response?.data)) {
          setAvailableLevels(response.data);
          if (showSuccess) {
            notify('success', 'Available levels refreshed successfully');
          }
        } else {
          setAvailableLevels([]);
          if (showSuccess) {
            notify('warning', 'No available levels found in API');
          }
        }
      } catch (error) {
        console.error('Error fetching available levels:', error);
        setAvailableLevels([]);
        if (showSuccess) {
          notify('warning', 'Failed to load available levels from API');
        }
      }
    },
    [notify],
  );

  const fetchMetricsFromApi = useCallback(
    async (showAlerts = true) => {
      try {
        const response = await CommHandler.request({
          url: `${config.apiBaseUrl}/slab-mapping-metrics/metric`,
          method: 'GET',
        });

        if (Array.isArray(response?.data)) {
          const apiMetrics = response.data;

          const levelGroups: Record<string, ApiMetricResponse[]> = {};
          apiMetrics.forEach((metric: ApiMetricResponse) => {
            if (!levelGroups[metric.level]) {
              levelGroups[metric.level] = [];
            }
            levelGroups[metric.level].push(metric);
          });

          const processedLevels: LevelMapping[] = Object.keys(levelGroups).map((levelString, index) => {
            const levelNumber = extractLevelNumber(levelString);
            const metricsForLevel = levelGroups[levelString];

            return {
              id: index + 1,
              level: `L${levelNumber}`,
              levelNumber,
              categories: metricsForLevel.map((metric) => metric.weightage.category.name),
              fullLevelString: levelString,
            };
          });

          processedLevels.sort((a, b) => b.levelNumber - a.levelNumber);
          setLevels(processedLevels);

          if (showAlerts) {
            notify('success', 'Slab mapping data refreshed successfully from API!');
          }
        } else {
          setLevels([]);
          if (showAlerts) {
            notify('info', 'No data found in API.');
          }
        }
      } catch (error) {
        console.error('Failed to load data from API', error);
        setLevels([]);
        if (showAlerts) {
          notify('warning', 'Failed to load data from API.');
        }
      }
    },
    [notify],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchAvailableLevels(false);
        await fetchMetricsFromApi(false);
      } catch (error) {
        notify('warning', 'Failed to load slab mapping data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fetchAvailableLevels, fetchMetricsFromApi, notify]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id: number) => {
    const levelData = levels.find((level) => level.id === id);
    if (levelData?.fullLevelString) {
      // Use URL-encoded full level string to handle special characters
      const encodedLevel = encodeURIComponent(levelData.fullLevelString);
      navigate(`/slab-mapping/level/${encodedLevel}`);
    }
  };

  const allDisplayLevels = useMemo(() => {
    const combined: LevelMapping[] = [];

    availableLevels.forEach((fullLevelString, index) => {
      if (typeof fullLevelString !== 'string') {
        return;
      }

      const levelNumber = extractLevelNumber(fullLevelString);
      const configuredLevel = levels.find((l) => l.fullLevelString === fullLevelString);

      if (configuredLevel) {
        combined.push({
          ...configuredLevel,
          fullLevelString,
        });
      } else {
        combined.push({
          id: Date.now() + index,
          level: `L${levelNumber}`,
          levelNumber,
          categories: [],
          isPlaceholder: true,
          fullLevelString,
        });
      }
    });

    combined.sort((a, b) => b.levelNumber - a.levelNumber);

    return combined;
  }, [availableLevels, levels]);

  const visibleLevels = useMemo(() => allDisplayLevels.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [allDisplayLevels, page, rowsPerPage]);

  const renderTableBody = () => {
    if (loading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={3} align="center">
              Loading...
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    if (allDisplayLevels.length === 0) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={3} align="center">
              No levels available. Please check the levels API configuration.
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    return (
      <TableBody>
        {visibleLevels.map((level) => (
          <TableRow
            key={level.fullLevelString || level.id}
            sx={{
              opacity: level.isPlaceholder ? 0.6 : 1,
              backgroundColor: level.isPlaceholder ? '#f5f5f5' : 'inherit',
            }}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>{level.fullLevelString || level.level || '-'}</Typography>
                {level.isPlaceholder && <Chip label="Not Configured" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
              </Box>
            </TableCell>
            <TableCell>
              {level.categories && level.categories.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {level.categories.map((cat) => (
                    <Chip key={`${level.fullLevelString || level.id}-${cat}`} label={cat} size="small" color="primary" sx={{ color: '#ffffff' }} />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {level.isPlaceholder ? 'Configure to add categories' : 'No categories'}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex' }}>
                {hasEditPermission && !level.isPlaceholder && (
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(level.id)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {hasEditPermission && level.isPlaceholder && (
                  <Tooltip title="Configure Level">
                    <IconButton
                      onClick={() => {
                        if (level.fullLevelString) {
                          const encodedLevel = encodeURIComponent(level.fullLevelString);
                          navigate(`/slab-mapping/level/${encodedLevel}`);
                        }
                      }}
                      color="success"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  return (
    <div style={{ width: '100%' }}>
      <StyledTableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="slab mapping table">
          <TableHead
            style={{
              background: '#e0ebf5',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <TableRow sx={{ height: '68px' }}>
              <TableCell
                sx={{
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#5A6473',
                  py: 1.5,
                  pl: 3,
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: '30%',
                }}
              >
                LEVELS
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#5A6473',
                  py: 1.5,
                  pl: 3,
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: '30%',
                }}
              >
                CATEGORIES
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#5A6473',
                  py: 1.5,
                  pl: 3,
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: '20%',
                }}
              >
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          {renderTableBody()}
        </Table>
      </StyledTableContainer>

      <TablePagination component="div" count={allDisplayLevels.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={[10, 25, 50]} />
    </div>
  );
};

export default SlabMapping;
